// ACHIEVEMENTS, QUESTS and CHALLENGES (owner 2026-11-02: "add challenges and achievements, maybe some quests").
// Pure data + rules + tracking: no DOM, no three (unit-tested in tests/feats.test.ts). The page is src/app/featsMenu.ts.
//
//  • ACHIEVEMENTS: 70 permanent one-time unlocks in seven categories. Each pays Fracture Shards once; a few also grant a
//    title the player can wear. Progress comes from this module's own lifetime counters (profile.feats.stats / sets), except
//    the two Encyclopedia ones, which read the Encyclopedia itself.
//  • QUESTS: 3 active slots. Slot 1 walks a STORY chain that teaches the systems; slots 2-3 are DAILY quests picked from
//    the local date (no server), so everyone gets the same two on the same day. Finishing one pays shards and refills the
//    slot (the next story step, or the day's next daily). Progress carries across runs.
//  • CHALLENGES: fixed-rule runs (run modifiers from src/core/mods.ts + a couple of challenge-only rules, ChallengeRules)
//    with a goal (survive N minutes, or defeat the final boss), a large first-clear shard reward and a badge.
//
// Overload (the risk trait, owner 2026-11-03) is NOT a run modifier: it never counts toward, or disqualifies, a modifier- or
// difficulty-based achievement; those are judged by sim.mods alone (tests/economyTraits.test.ts).
// Difficulty (Run Setup presets, src/core/mods.ts): the Modifiers category has Win-on-Hard / Win-on-Brutal achievements,
// judged from the modifiers actually on (a Custom mix that contains every Hard / Brutal modifier counts). Relaxed is a
// gentle modifier with a shard penalty: runs with it on never count for any modifier- or difficulty-based achievement or
// for the modifier quests. Endless (src/core/endless.ts): the tier reached feeds Beyond the End and the tier achievements.
//
// Tracking only READS the Sim (state + this step's events) from the app layer, so the simulation stays deterministic.
// TRAINING runs never progress anything here: FeatTracker.beginRun(sim, { training }) or the Sim's training sandbox
// (SimOptions.training / sim.training) turns the tracker off for that run (see isTrainingRun).
//
// Save: profile.feats (src/app/save.ts) is OPTIONAL. Older saves have none and get an empty one; unknown / missing / junk
// fields fall back to defaults (migrateFeats), so no save-version bump is needed. It travels with save export / import.
import type { Sim } from '../core/sim';
import { DT } from '../core/skillStats';
import { INNATE, MAX_WEAPON_LEVEL, MAX_WEAPONS, WEAPONS, type WeaponId } from '../core/content';
import { FUSION_IDS } from '../core/fusions';
import { MOD_IDS, difficultyDef, modDef, modList, type ChallengeRules, type ModId, type RunMods } from '../core/mods';
import { ENDLESS_TIERS, endlessMilestoneAt, endlessTierName, roman } from '../core/endless';
import { MAPS } from '../maps/registry';
import { CODEX_TABS, counts, type Codex } from './codex';
import type { BadgeFrame, BadgeSpec, BadgeTier, Glyph } from './badges';

// ---------------------------------------------------------------------------------------------------------------------
// Saved data
// ---------------------------------------------------------------------------------------------------------------------
export interface QuestSlot { id: string; prog: number }
/** day = local date the dailies were rolled for (YYYY-MM-DD); story = story steps finished; n = dailies handed out today */
export interface QuestState { day: string; slots: QuestSlot[]; story: number; n: number; done: number }
export interface ChallengeRec { tries: number; done: number; /** first clear (ms since epoch, 0 = never) */ first: number; /** best progress 0..1 */ best: number }
export interface FeatsSave {
  /** achievement id → unlock time (ms since epoch) */
  unlocked: Record<string, number>;
  /** lifetime sums, best-single-run values and 0/1 flags (keys: STAT) */
  stats: Record<string, number>;
  /** distinct things done (maps won, modifiers won with, skills evolved, fusions made, challenges cleared …) */
  sets: Record<string, string[]>;
  quests: QuestState;
  challenges: Record<string, ChallengeRec>;
  /** equipped title: id of an unlocked achievement that grants one ('' = none) */
  title: string;
  /** unlocked since the Achievements page was last opened (NEW dot on the title menu) */
  fresh: string[];
  /** lifetime shards paid by achievements, quests and challenges */
  earned: number;
}

export function emptyFeats(): FeatsSave {
  return { unlocked: {}, stats: {}, sets: {}, quests: { day: '', slots: [], story: 0, n: 0, done: 0 }, challenges: {}, title: '', fresh: [], earned: 0 };
}

const ID_OK = /^[A-Za-z0-9_:-]{1,48}$/;
const cnt = (x: unknown) => (typeof x === 'number' && Number.isFinite(x) && x >= 0 ? x : 0);
const isObj = (x: unknown): x is Record<string, unknown> => !!x && typeof x === 'object' && !Array.isArray(x);
/** Validate any saved shape (or none). Unknown ids are kept (content may be added later; the page lists only known ones). */
export function migrateFeats(raw: unknown): FeatsSave {
  const f = emptyFeats();
  if (!isObj(raw)) return f;
  if (isObj(raw.unlocked)) for (const [k, v] of Object.entries(raw.unlocked)) if (ID_OK.test(k)) f.unlocked[k] = cnt(v);
  if (isObj(raw.stats)) for (const [k, v] of Object.entries(raw.stats)) if (ID_OK.test(k) && cnt(v) > 0) f.stats[k] = cnt(v);
  if (isObj(raw.sets)) for (const [k, v] of Object.entries(raw.sets)) {
    if (ID_OK.test(k) && Array.isArray(v)) f.sets[k] = [...new Set(v.filter((s): s is string => typeof s === 'string' && ID_OK.test(s)))].slice(0, 200);
  }
  if (isObj(raw.quests)) {
    const q = raw.quests;
    f.quests.day = typeof q.day === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(q.day) ? q.day : '';
    f.quests.story = Math.floor(cnt(q.story)); f.quests.n = Math.floor(cnt(q.n)); f.quests.done = Math.floor(cnt(q.done));
    if (Array.isArray(q.slots)) f.quests.slots = q.slots.filter(isObj).filter((s) => typeof s.id === 'string' && !!QUEST_BY_ID[s.id as string])
      .slice(0, QUEST_SLOTS).map((s) => ({ id: s.id as string, prog: cnt(s.prog) }));
  }
  if (isObj(raw.challenges)) for (const [k, v] of Object.entries(raw.challenges)) {
    if (ID_OK.test(k) && isObj(v)) f.challenges[k] = { tries: Math.floor(cnt(v.tries)), done: Math.floor(cnt(v.done)), first: cnt(v.first), best: Math.min(1, cnt(v.best)) };
  }
  f.title = typeof raw.title === 'string' && f.unlocked[raw.title] !== undefined && !!ACH_BY_ID[raw.title]?.titleReward ? raw.title : '';
  if (Array.isArray(raw.fresh)) f.fresh = raw.fresh.filter((s): s is string => typeof s === 'string' && f.unlocked[s] !== undefined).slice(-80);
  f.earned = Math.floor(cnt(raw.earned));
  return f;
}

// ---------------------------------------------------------------------------------------------------------------------
// Stat keys (what the tracker records)
// ---------------------------------------------------------------------------------------------------------------------
/** lifetime sums */
export type SumStat = 'kills' | 'elites' | 'bosses' | 'damage' | 'caches' | 'chests' | 'fields' | 'surges' | 'evolves' | 'fusions'
  | 'runs' | 'wins' | 'seconds' | 'challenges' | 'modRuns';
/** best value reached in a single run */
export type RunStat = 'runKills' | 'runDamage' | 'runTime' | 'runLevel' | 'runEvolves' | 'runCaches' | 'endless' | 'endlessTier' | 'elites10' | 'runForm';
/** 0/1 flags */
export type FlagStat = 'surge3' | 'surge4' | 'surge6' | 'surge8' | 'slots6' | 'maxed6' | 'winNight' | 'winEvo3' | 'winFusion' | 'winNoSurge' | 'winNoPower' | 'win30'
  | 'mods3' | 'modsAll' | 'still' | 'thread' | 'untouched' | 'late' | 'hoard';
export type SetKey = 'mapsWon' | 'mapsPlayed' | 'modsWon' | 'evolved' | 'fused' | 'challenges' | 'diffWon' | 'brutalMaps';

/** a win only counts on runs of 5:00 or longer (the 1:30 / 3:00 test lengths don't) */
export const MIN_WIN_LEN = 300;
/** Elite Sweep window (s) */
export const ELITE_WINDOW = 10;
/** Eye of the Storm: seconds standing still · By a Thread: health share and seconds to live after it · Untouchable: s */
export const STILL_SEC = 20, THREAD_HP = 0.05, THREAD_SEC = 30, UNTOUCHED_SEC = 180;
/** skills that can evolve (every skill in the game; retired ones excluded) */
/** the 'raise the stakes' modifiers (positive shard bonus): everything except Relaxed. Only these count for the
 *  modifier achievements, the modifier quests and The Gauntlet. */
export const STAKE_MODS: readonly ModId[] = MOD_IDS.filter((id) => modDef(id).bonus > 0);
/** the difficulty a won run counts as for achievements: from the modifiers actually on. null = Relaxed on (counts for none).
 *  'brutal' / 'hard' = every modifier of that preset is on (extra ones allowed); 'normal' = no modifiers; else 'custom'. */
export function winDifficulty(m: RunMods | undefined): 'normal' | 'hard' | 'brutal' | 'custom' | null {
  if (m?.relaxed) return null;
  const on = modList(m), all = (id: 'hard' | 'brutal') => difficultyDef(id).mods.every((x) => on.includes(x));
  return all('brutal') ? 'brutal' : all('hard') ? 'hard' : on.length ? 'custom' : 'normal';
}
/** endless tier achievements: tier number → [id, title, reward, glyph, brief, wearable title] */
export const ENDLESS_TIER_ACH: readonly { tier: number; id: string; title: string; reward: number; glyph: Glyph; brief: string; titleReward?: string }[] = [
  { tier: 3, id: 'endlessT3', title: 'Crowned in Corona', reward: 200, glyph: 'sun', brief: 'gold shield, a white-gold corona ring flaring around a dark eclipsed core' },
  { tier: 5, id: 'endlessT5', title: 'Paragon Standing', reward: 350, glyph: 'star', titleReward: 'the Paragon', brief: 'prism shield, a cyan eight-point star standing on a stepped dais of five tiers' },
  { tier: ENDLESS_TIERS.length + 1, id: 'endlessLimit', title: 'Past Every Limit', reward: 500, glyph: 'infinity', titleReward: 'the Limitless', brief: 'prism shield, a magenta infinity loop breaking through the shield rim into open sky' },
];
export const EVOLVABLE: readonly WeaponId[] = (Object.keys(WEAPONS) as WeaponId[]).filter((id) => !WEAPONS[id].retired);

// ---------------------------------------------------------------------------------------------------------------------
// ACHIEVEMENTS
// ---------------------------------------------------------------------------------------------------------------------
export type FeatCat = 'survival' | 'combat' | 'build' | 'mastery' | 'modifiers' | 'exploration' | 'secrets';
export const FEAT_CATS: readonly FeatCat[] = ['survival', 'combat', 'build', 'mastery', 'modifiers', 'exploration', 'secrets'];
export const CAT_LABEL: Record<FeatCat, string> = { survival: 'SURVIVAL', combat: 'COMBAT', build: 'BUILD', mastery: 'MASTERY', modifiers: 'MODIFIERS', exploration: 'EXPLORATION', secrets: 'SECRETS' };
const CAT_FRAME: Record<FeatCat, BadgeFrame> = { survival: 'shield', combat: 'round', build: 'hex', mastery: 'star', modifiers: 'diamond', exploration: 'octa', secrets: 'secret' };
export const tierFor = (reward: number): BadgeTier => (reward <= 30 ? 0 : reward <= 100 ? 1 : reward <= 250 ? 2 : 3);

export interface AchDef {
  id: string; cat: FeatCat; title: string; desc: string;
  /** progress needed (1 for yes/no achievements) */
  goal: number;
  /** Fracture Shards paid once on unlock */
  reward: number;
  /** a title the player can wear once unlocked */
  titleReward?: string;
  /** shown as ??? until unlocked */
  hidden?: boolean;
  badge: BadgeSpec;
  /** one-line brief for the art partner (painted badge) */
  brief: string;
  /** current progress */
  prog(f: FeatsSave, codex: Codex): number;
}

const st = (k: SumStat | RunStat | FlagStat) => (f: FeatsSave) => f.stats[k] ?? 0;
const sz = (k: SetKey) => (f: FeatsSave) => f.sets[k]?.length ?? 0;
const has = (k: SetKey, v: string) => (f: FeatsSave) => (f.sets[k]?.includes(v) ? 1 : 0);
const codexTotals = (c: Codex) => { let found = 0, total = 0; for (const t of CODEX_TABS) { const q = counts(t, c); found += q.found; total += q.total; } return { found, total }; };

type Raw = Omit<AchDef, 'badge' | 'cat'> & { glyph: Glyph };
const mk = (cat: FeatCat, list: Raw[]): AchDef[] => list.map(({ glyph, ...a }) => ({ ...a, cat, badge: { frame: CAT_FRAME[cat], tier: tierFor(a.reward), glyph } }));

/** map id → achievement title for beating that map's final boss (unknown maps: '<Map> Conqueror') */
const MAP_TITLE: Record<string, string> = {
  'canyon-meadow': 'Meadowguard', 'rust-canyon': 'Ore Breaker', 'ember-wastes': 'Kiln Quencher', 'floating-isles': 'Leviathan Grounded',
  'verdant-alien': 'Grove Silencer', 'shattered-metropolis': 'Curfew Lifted', 'glacier-ruins': 'Thaw Bringer', 'crystal-desert': 'Duneglass Shattered',
};
const cap = (s: string) => s.toLowerCase().replace(/\b[a-z]/g, (m) => m.toUpperCase());
const READY_MAPS = MAPS.filter((m) => m.status === 'ready');

/** the Win-with-<modifier> achievements (original titles) */
const MOD_ACH: Record<Exclude<ModId, 'relaxed'>, { title: string; glyph: Glyph; brief: string }> = {
  hyper: { title: 'Overclocked', glyph: 'fast', brief: 'twin forward chevrons streaking off a blue-silver diamond, motion lines' },
  swarm: { title: 'Swarm Breaker', glyph: 'swarm', brief: 'a cloud of tiny glowing dots split by one bright crack' },
  tough: { title: 'Hardened', glyph: 'shield', brief: 'a dented kite shield with glowing rivets' },
  hurry: { title: 'Against the Clock', glyph: 'clock', brief: 'a cracked clock face with the hands blurred forward' },
  noheal: { title: 'Iron Will', glyph: 'nocross', brief: 'a medical cross struck through by a bar of iron' },
  elites: { title: 'Elite Welcome', glyph: 'crown', brief: 'a spiked gold crown hanging on a broken standard' },
  night: { title: 'Endless Dusk', glyph: 'moon', brief: 'a crescent moon over a dark diamond with faint stars' },
};

export const ACHIEVEMENTS: readonly AchDef[] = [
  ...mk('survival', [
    { id: 'surv5', title: 'First Light', desc: 'Survive 5 minutes in a run.', goal: 300, reward: 20, glyph: 'hourglass', prog: st('runTime'), brief: 'bronze shield, a small hourglass with the first grains falling' },
    { id: 'surv10', title: 'Holding the Line', desc: 'Survive 10 minutes in a run.', goal: 600, reward: 40, glyph: 'hourglass', prog: st('runTime'), brief: 'silver shield, hourglass half run through, a line drawn under it' },
    { id: 'surv20', title: 'Unbroken', desc: 'Survive 20 minutes in a run.', goal: 1200, reward: 80, glyph: 'hourglass', prog: st('runTime'), brief: 'silver shield, a cracked hourglass still holding its sand' },
    { id: 'surv30', title: 'Full Shift', desc: 'Survive 30 minutes in a run.', goal: 1800, reward: 150, glyph: 'clock', titleReward: 'the Unbroken', prog: st('runTime'), brief: 'gold shield, a full clock face with a sunrise behind it' },
    { id: 'win1', title: 'Tyrantfall', desc: 'Win a run: defeat the final boss (runs of 5:00 or longer).', goal: 1, reward: 100, glyph: 'crown', prog: st('wins'), brief: 'silver shield, a toppled dark crown split in two' },
    { id: 'wins10', title: 'Seasoned Survivor', desc: 'Win 10 runs.', goal: 10, reward: 250, glyph: 'crown', titleReward: 'the Seasoned', prog: st('wins'), brief: 'gold shield, a laurel of ten small crowns around a bright core' },
    { id: 'winNight', title: 'Moonlit Victory', desc: 'Win a run at night (the Night time of day, or Eternal Night).', goal: 1, reward: 80, glyph: 'moon', prog: st('winNight'), brief: 'silver shield, a crescent moon cradling a small crown' },
    { id: 'endless10', title: 'Beyond the End', desc: `In Endless mode, survive 10 minutes after the final boss falls (reach Tier II · ${cap(endlessTierName(2))}).`, goal: 600, reward: 150, glyph: 'infinity',
      prog: (f) => Math.max(f.stats.endless ?? 0, (f.stats.endlessTier ?? 0) >= 2 ? endlessMilestoneAt(2) : 0), brief: 'gold shield, a glowing infinity loop running off the edge' },
    ...ENDLESS_TIER_ACH.map((e): Raw => ({
      id: e.id, title: e.title, goal: e.tier, reward: e.reward, glyph: e.glyph, titleReward: e.titleReward, prog: st('endlessTier'), brief: e.brief,
      desc: e.tier > ENDLESS_TIERS.length ? `In Endless mode, reach a ${cap(endlessTierName(e.tier))} tier (Tier ${roman(e.tier)}, ${Math.round(endlessMilestoneAt(e.tier) / 60)} minutes after the final boss).`
        : `In Endless mode, reach Tier ${roman(e.tier)} · ${cap(endlessTierName(e.tier))} (${Math.round(endlessMilestoneAt(e.tier) / 60)} minutes after the final boss).`,
    })),
    ...READY_MAPS.map((m): Raw => ({
      id: `map_${m.id}`, title: MAP_TITLE[m.id] ?? `${m.name} Conqueror`, desc: `Win on ${m.name}: defeat ${cap(m.boss.name)}.`, goal: 1, reward: 80, glyph: 'pin',
      prog: has('mapsWon', m.id), brief: `silver shield, a map pin planted in a silhouette of the ${cap(m.boss.name)}`,
    })),
  ]),
  ...mk('combat', [
    { id: 'kills500', title: 'Crowd Control', desc: 'Defeat 500 foes in one run.', goal: 500, reward: 30, glyph: 'claws', prog: st('runKills'), brief: 'bronze round badge, three energy slashes across it' },
    { id: 'kills1500', title: 'Tidebreaker', desc: 'Defeat 1,500 foes in one run.', goal: 1500, reward: 75, glyph: 'wave', prog: st('runKills'), brief: 'silver round badge, a breaking wave of tiny foes' },
    { id: 'kills3000', title: "Horde's End", desc: 'Defeat 3,000 foes in one run.', goal: 3000, reward: 150, glyph: 'claws', prog: st('runKills'), brief: 'gold round badge, deep claw slashes through a field of silhouettes' },
    { id: 'kills10k', title: 'Ten Thousand Falls', desc: 'Defeat 10,000 foes in total.', goal: 10000, reward: 100, glyph: 'burst', prog: st('kills'), brief: 'silver round badge, a starburst made of 10 rays of falling motes' },
    { id: 'dmg100k', title: 'Heavy Hitter', desc: 'Deal 100,000 damage in one run.', goal: 100000, reward: 40, glyph: 'blade', prog: st('runDamage'), brief: 'silver round badge, an upright energy blade with sparks' },
    { id: 'dmg1m', title: 'Overwhelming Force', desc: 'Deal 1,000,000 damage in one run.', goal: 1000000, reward: 150, glyph: 'blade', prog: st('runDamage'), brief: 'gold round badge, a blade splitting the badge rim with light' },
    { id: 'elites3', title: 'Elite Sweep', desc: `Defeat 3 elites within ${ELITE_WINDOW} seconds.`, goal: 3, reward: 75, glyph: 'target', prog: st('elites10'), brief: 'silver round badge, a crosshair with three small crowns in it' },
    { id: 'elites100', title: 'Elite Hunter', desc: 'Defeat 100 elites in total.', goal: 100, reward: 100, glyph: 'target', prog: st('elites'), brief: 'silver round badge, a hunter\'s crosshair over a gold crest' },
    { id: 'surge3', title: 'Azure Ascent', desc: 'Reach Surge form III · Azure.', goal: 1, reward: 50, glyph: 'bolt', prog: st('surge3'), brief: 'silver round badge, a lightning bolt in a blue aura flare' },
    // SURGE FORMS (owner 2026-11-03): the eight forms; feats at the first storm form, the black-flame form (VI) and the top form
    { id: 'surge4', title: 'Storm Awakened', desc: 'Reach Surge form IV · Verdant Tempest.', goal: 1, reward: 75, glyph: 'bolt', prog: st('surge4'), brief: 'silver round badge, forked emerald lightning around a green aura' },
    { id: 'surge6', title: 'Black Flame', desc: 'Reach Surge form VI · Obsidian Inferno.', goal: 1, reward: 150, glyph: 'burst', prog: st('surge6'), brief: 'gold round badge, a black flame around a molten orange core, glowing cracks in the ground beneath' },
    { id: 'surge8', title: 'Shatterlight', desc: 'Reach Surge form VIII · Shatterlight, the final form.', goal: 1, reward: 300, glyph: 'star', titleReward: 'the Shatterlight', prog: st('surge8'), brief: 'prism round badge, a white-gold star with crystal shards orbiting it and fractured air behind' },
  ]),
  ...mk('build', [
    { id: 'evo1', title: 'First Evolution', desc: 'Evolve a skill.', goal: 1, reward: 30, glyph: 'chevrons', prog: sz('evolved'), brief: 'bronze hexagon, a rising chevron with a small star on top' },
    { id: 'evoRun3', title: 'Triple Ascension', desc: 'Evolve 3 skills in one run.', goal: 3, reward: 75, glyph: 'chevrons', prog: st('runEvolves'), brief: 'silver hexagon, three stacked rising chevrons' },
    { id: 'evoAll', title: 'Every Form Revealed', desc: 'Evolve every skill at least once.', goal: EVOLVABLE.length, reward: 400, glyph: 'gem', titleReward: 'the Ascended', prog: sz('evolved'), brief: 'prism hexagon, a cut gem reflecting many small skill sigils' },
    { id: 'fuse1', title: 'Two Become One', desc: 'Make your first Skill Fusion.', goal: 1, reward: 50, glyph: 'fuse', prog: sz('fused'), brief: 'silver hexagon, two overlapping rings whose overlap glows emerald' },
    { id: 'fuse10', title: 'Fusion Adept', desc: 'Make 10 different Skill Fusions.', goal: 10, reward: 150, glyph: 'fuse', prog: sz('fused'), brief: 'gold hexagon, interlocked rings ringed by ten emerald sparks' },
    { id: 'fuseAll', title: 'Grand Synthesis', desc: 'Make every Skill Fusion.', goal: FUSION_IDS.length, reward: 500, glyph: 'fuse', titleReward: 'the Synthesist', prog: sz('fused'), brief: 'prism hexagon, a perfect emerald sphere formed from two rings' },
    { id: 'slots6', title: 'Full Arsenal', desc: 'Hold 6 skills at once.', goal: 1, reward: 30, glyph: 'grid', prog: st('slots6'), brief: 'bronze hexagon, six filled slot tiles' },
    { id: 'maxed6', title: 'Perfect Lattice', desc: 'Fill all 6 skill slots with max-rank, evolved or fused skills.', goal: 1, reward: 150, glyph: 'grid', prog: st('maxed6'), brief: 'gold hexagon, six slot tiles all lit gold and linked' },
    { id: 'level50', title: 'Ascendant', desc: 'Reach level 50 in one run.', goal: 50, reward: 100, glyph: 'chevrons', prog: st('runLevel'), brief: 'silver hexagon, a tall stack of chevrons breaking the top edge' },
  ]),
  ...mk('mastery', [
    { id: 'winEvo3', title: 'Triple Crown', desc: 'Win a run with 3 or more evolved skills.', goal: 1, reward: 150, glyph: 'star', prog: st('winEvo3'), brief: 'gold eight-point star, three small stars on a crown band' },
    { id: 'winFusion', title: 'Fused Victory', desc: 'Win a run with a Skill Fusion in your build.', goal: 1, reward: 150, glyph: 'fuse', prog: st('winFusion'), brief: 'gold eight-point star, interlocked rings wearing a crown' },
    { id: 'winNoSurge', title: 'Steady Hands', desc: 'Win a run without entering Surge Form.', goal: 1, reward: 200, glyph: 'eye', prog: st('winNoSurge'), brief: 'gold eight-point star, a calm open eye with no aura' },
    { id: 'winNoPower', title: 'Raw Talent', desc: 'Win a run with Power Ups turned off (Run Setup).', goal: 1, reward: 200, glyph: 'one', prog: st('winNoPower'), brief: 'gold eight-point star, a bare numeral one carved in stone' },
    { id: 'win30', title: 'Marathon Champion', desc: 'Win a full 30:00 run.', goal: 1, reward: 250, glyph: 'sun', titleReward: 'the Enduring', prog: st('win30'), brief: 'gold eight-point star, a sun rising over a finish line' },
    { id: 'ch1', title: 'Challenger', desc: 'Complete a Challenge.', goal: 1, reward: 50, glyph: 'key', prog: sz('challenges'), brief: 'silver eight-point star, an old key turning in a crest' },
    { id: 'chAll', title: 'Challenge Master', desc: 'Complete every Challenge.', goal: 0 /* set below */, reward: 400, glyph: 'crown', titleReward: 'the Unyielding', prog: sz('challenges'), brief: 'prism eight-point star, a crown made of every challenge crest' },
  ]),
  ...mk('modifiers', [
    ...STAKE_MODS.map((id): Raw => { const m = MOD_ACH[id as Exclude<ModId, 'relaxed'>]; return { id: `mod_${id}`, title: m.title, desc: `Win a run with ${modDef(id).name} on (not with Relaxed).`, goal: 1, reward: 60, glyph: m.glyph, prog: has('modsWon', id), brief: `silver diamond, ${m.brief}` }; }),
    { id: 'mods3', title: 'Stacked Odds', desc: 'Win a run with 3 or more modifiers on (Relaxed does not count).', goal: 1, reward: 150, glyph: 'dots3', prog: st('mods3'), brief: 'gold diamond, three stacked modifier pips glowing red' },
    { id: 'diffHard', title: 'Hard Earned', desc: 'Win a run on Hard (Swarm, Tougher Foes and Early Elites all on).', goal: 1, reward: 120, glyph: 'tower', prog: has('diffWon', 'hard'), brief: 'silver diamond, a stone watchtower standing in a rising tide of red pips' },
    { id: 'diffBrutal', title: 'Brutal Ascent', desc: 'Win a run on Brutal (all six Brutal modifiers on).', goal: 1, reward: 300, glyph: 'claws', titleReward: 'the Brutal', prog: has('diffWon', 'brutal'), brief: 'gold diamond, three jagged claw marks torn through a cracked crown' },
    { id: 'diffLadder', title: 'Every Rung', desc: 'Win on Normal (no modifiers), on Hard and on Brutal.', goal: 3, reward: 200, glyph: 'chevrons', prog: (f) => ['normal', 'hard', 'brutal'].filter((d) => f.sets.diffWon?.includes(d)).length, brief: 'gold diamond, three chevrons stacked like ladder rungs, each lit a hotter colour' },
    { id: 'brutalMaps', title: 'No Safe Ground', desc: 'Win on Brutal on 3 different arenas.', goal: 3, reward: 400, glyph: 'compass', prog: sz('brutalMaps'), brief: 'prism diamond, a cracked compass rose with three blood-red pins in it' },
    { id: 'modsAll', title: 'Gauntlet of Seven', desc: 'Win a run with all seven raise-the-stakes modifiers on.', goal: 1, reward: 500, glyph: 'seven', titleReward: 'the Relentless', prog: st('modsAll'), brief: 'prism diamond, seven lit pips in a ring around a bright core' },
  ]),
  ...mk('exploration', [
    { id: 'cache1', title: 'Relic Seeker', desc: 'Open a relic cache.', goal: 1, reward: 15, glyph: 'chest', prog: st('caches'), brief: 'bronze octagon, a small relic cache with its lid cracked open' },
    { id: 'cache50', title: 'Cache Collector', desc: 'Open 50 relic caches in total.', goal: 50, reward: 100, glyph: 'chest', prog: st('caches'), brief: 'silver octagon, a pile of opened relic caches spilling light' },
    { id: 'maps', title: 'World Walker', desc: 'Play a run on every arena.', goal: READY_MAPS.length, reward: 100, glyph: 'compass', prog: sz('mapsPlayed'), brief: 'silver octagon, a compass rose with one gem per arena' },
    { id: 'codexHalf', title: 'Field Notes', desc: 'Discover half of the Encyclopedia.', goal: 0 /* set below */, reward: 75, glyph: 'book', prog: (_f, c) => codexTotals(c).found, brief: 'silver octagon, an open notebook with sketches' },
    { id: 'codexAll', title: 'Complete Archive', desc: 'Discover every entry in the Encyclopedia.', goal: 0 /* set below */, reward: 300, glyph: 'book', titleReward: 'the Archivist', prog: (_f, c) => codexTotals(c).found, brief: 'prism octagon, a glowing tome with every page filled' },
  ]),
  ...mk('secrets', [
    { id: 'still', title: 'Eye of the Storm', desc: `Stand completely still for ${STILL_SEC} seconds during a run.`, goal: 1, reward: 50, glyph: 'spiral', hidden: true, prog: st('still'), brief: 'silver round badge with a dashed ring, a calm spiral eye inside a storm' },
    { id: 'thread', title: 'By a Thread', desc: `Fall below ${THREAD_HP * 100}% health and survive ${THREAD_SEC} more seconds.`, goal: 1, reward: 75, glyph: 'heart', hidden: true, prog: st('thread'), brief: 'silver dashed-ring badge, a cracked heart held by one thread' },
    { id: 'untouched', title: 'Untouchable', desc: 'Survive the first 3 minutes of a run without taking a hit.', goal: 1, reward: 75, glyph: 'shield', hidden: true, prog: st('untouched'), brief: 'silver dashed-ring badge, a spotless shield with blows glancing off' },
    { id: 'late', title: 'Midnight Oil', desc: 'Finish a run between midnight and 4 a.m. (your clock).', goal: 1, reward: 40, glyph: 'moon', hidden: true, prog: st('late'), brief: 'silver dashed-ring badge, a lamp burning under a crescent moon' },
    { id: 'hoard', title: 'Shard Hoard', desc: 'Hold 2,000 Fracture Shards at once.', goal: 1, reward: 30, glyph: 'gem', hidden: true, prog: st('hoard'), brief: 'bronze dashed-ring badge, a heap of glowing shards' },
  ]),
];
export const ACH_BY_ID: Record<string, AchDef> = Object.fromEntries(ACHIEVEMENTS.map((a) => [a.id, a]));
/** shards held at once for Shard Hoard */
export const HOARD_SHARDS = 2000;

// ---------------------------------------------------------------------------------------------------------------------
// QUESTS
// ---------------------------------------------------------------------------------------------------------------------
export type QuestStat = SumStat | RunStat;
export interface QuestDef {
  id: string; title: string; desc: string; stat: QuestStat; goal: number; reward: number;
  /** total = adds up across runs; run = the best single run since the quest was given */
  scope: 'total' | 'run';
  kind: 'story' | 'daily';
}
const q = (kind: QuestDef['kind'], id: string, title: string, desc: string, stat: QuestStat, goal: number, reward: number, scope: QuestDef['scope'] = 'total'): QuestDef => ({ id, title, desc, stat, goal, reward, scope, kind });
/** STORY: one at a time, in order; each teaches a system */
export const STORY: readonly QuestDef[] = [
  q('story', 's_survive', 'First Steps', 'Survive 3 minutes in a run.', 'runTime', 180, 20, 'run'),
  q('story', 's_level', 'Growing Stronger', 'Reach level 10 in a run.', 'runLevel', 10, 25, 'run'),
  q('story', 's_surge', 'Awaken the Surge', 'Enter Surge Form 3 times (fill the Ki bar, then press Surge).', 'surges', 3, 30),
  q('story', 's_caches', 'Relic Hunter', 'Open 2 relic caches in one run.', 'runCaches', 2, 35, 'run'),
  q('story', 's_evolve', 'Evolution', 'Evolve a skill (max rank + its partner trait).', 'evolves', 1, 50),
  q('story', 's_mod', 'Raise the Stakes', 'Finish a run with any modifier on (Run Setup; Relaxed does not count).', 'modRuns', 1, 40),
  q('story', 's_fuse', 'Fuse Two Skills', 'Make a Skill Fusion (two partner skills at max rank).', 'fusions', 1, 75),
  q('story', 's_boss', 'Guardian Down', 'Defeat a guardian or boss.', 'bosses', 1, 60),
  q('story', 's_challenge', 'Accept a Challenge', 'Complete a Challenge (Achievements › Challenges).', 'challenges', 1, 100),
  q('story', 's_win', 'The Last Light', 'Win a run.', 'wins', 1, 120),
];
/** DAILY: two a day from the local date, refilled from the same day's list as they are finished */
export const DAILY: readonly QuestDef[] = [
  q('daily', 'd_kills', 'Thin the Horde', 'Defeat 1,500 foes.', 'kills', 1500, 30),
  q('daily', 'd_elites', 'Elite Bounty', 'Defeat 8 elites.', 'elites', 8, 35),
  q('daily', 'd_caches', 'Cache Run', 'Open 4 relic caches.', 'caches', 4, 30),
  q('daily', 'd_surge', 'Surge Practice', 'Enter Surge Form 5 times.', 'surges', 5, 30),
  q('daily', 'd_form', 'Rising Form', 'Reach Surge form IV (Verdant Tempest) or higher in one run.', 'runForm', 4, 40, 'run'), // SURGE FORMS
  q('daily', 'd_time', 'Long Watch', 'Survive 25 minutes in total.', 'seconds', 1500, 35),
  q('daily', 'd_evolve', 'Ascend', 'Evolve 2 skills.', 'evolves', 2, 40),
  q('daily', 'd_damage', 'Damage Dealer', 'Deal 400,000 damage.', 'damage', 400000, 35),
  q('daily', 'd_fields', 'Scavenger', 'Pick up 6 field items.', 'fields', 6, 25),
  q('daily', 'd_boss', 'Giant Hunt', 'Defeat 2 guardians or bosses.', 'bosses', 2, 45),
  q('daily', 'd_level', 'Quick Study', 'Reach level 25 in one run.', 'runLevel', 25, 35, 'run'),
  q('daily', 'd_runtime', 'Endurance', 'Survive 12 minutes in one run.', 'runTime', 720, 40, 'run'),
  q('daily', 'd_mod', 'Handicapped', 'Finish 2 runs with a modifier on (not Relaxed).', 'modRuns', 2, 35),
  q('daily', 'd_chests', 'Chest Hunter', 'Open 2 chests.', 'chests', 2, 30),
  q('daily', 'd_fuse', 'Fusion Practice', 'Make a Skill Fusion.', 'fusions', 1, 50),
  q('daily', 'd_win', 'Daily Victory', 'Win a run.', 'wins', 1, 60),
  q('daily', 'd_cacheRun', 'Lucky Streak', 'Open 3 relic caches in one run.', 'runCaches', 3, 35, 'run'),
];
export const QUEST_BY_ID: Record<string, QuestDef> = Object.fromEntries([...STORY, ...DAILY].map((d) => [d.id, d]));
export const QUEST_SLOTS = 3;

/** local calendar day, YYYY-MM-DD */
export function dayKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
/** ms until the next local midnight (daily refresh) */
export function msToNextDay(d: Date): number { const n = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1); return n.getTime() - d.getTime(); }
function hash(s: string): number { let h = 0x811c9dc5; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0; return h; }
/** the n-th daily quest of a day (deterministic), skipping ids already active */
export function dailyPick(day: string, n: number, active: readonly string[]): QuestDef {
  const start = hash(`${day}#${n}`) % DAILY.length;
  for (let k = 0; k < DAILY.length; k++) { const d = DAILY[(start + k) % DAILY.length]; if (!active.includes(d.id)) return d; }
  return DAILY[start];
}
/** Bring the quest slots up to date for `day`: a new day replaces the dailies; the story slot keeps its step. */
export function rollQuests(qs: QuestState, day: string): void {
  const story = qs.story < STORY.length ? STORY[qs.story] : null;
  if (qs.day !== day) {
    qs.day = day; qs.n = 0;
    const keep = qs.slots[0] && story && qs.slots[0].id === story.id ? qs.slots[0] : null; // story progress survives the night
    qs.slots = [keep ?? (story ? { id: story.id, prog: 0 } : null)].filter((s): s is QuestSlot => !!s);
  }
  // slot 0 = the story step while the story lasts
  if (story && qs.slots[0]?.id !== story.id) qs.slots.unshift({ id: story.id, prog: 0 });
  qs.slots = qs.slots.filter((s, i, a) => !!QUEST_BY_ID[s.id] && a.findIndex((o) => o.id === s.id) === i).slice(0, QUEST_SLOTS);
  while (qs.slots.length < QUEST_SLOTS) qs.slots.push({ id: dailyPick(day, qs.n++, qs.slots.map((s) => s.id)).id, prog: 0 });
}

// ---------------------------------------------------------------------------------------------------------------------
// CHALLENGES
// ---------------------------------------------------------------------------------------------------------------------
export type ChallengeGoal = { kind: 'survive'; sec: number } | { kind: 'win' };
export interface ChallengeDef {
  id: string; name: string; desc: string;
  mods: RunMods; rules?: ChallengeRules;
  /** Power Ups (Workshop ranks) off for the run */
  noPowerUps?: boolean;
  /** run length (final boss time, s) */
  bossTime: number;
  goal: ChallengeGoal;
  /** first-clear shards */
  reward: number;
  badge: BadgeSpec; brief: string;
}
const chb = (glyph: Glyph, reward: number): BadgeSpec => ({ frame: 'crest', tier: tierFor(reward), glyph });
export const CHALLENGES: readonly ChallengeDef[] = [
  { id: 'glass', name: 'Glass Cannon', desc: 'Start with Spirit Bolt and 3 random skills, but foes are tougher and nothing heals you.', mods: { tough: true, noheal: true }, rules: { startSkills: 3 }, bossTime: 900, goal: { kind: 'survive', sec: 600 }, reward: 300, badge: chb('burst', 300), brief: 'gold crest, a cracked glass cannon shell bursting with light' },
  { id: 'speed', name: 'Speed Demon', desc: 'Hyper pace and a Hurried schedule: everything is faster and the final boss comes early.', mods: { hyper: true, hurry: true }, bossTime: 900, goal: { kind: 'win' }, reward: 350, badge: chb('fast', 350), brief: 'prism crest, twin chevrons outrunning a clock hand' },
  { id: 'onetrick', name: 'One Trick', desc: 'One skill slot: Spirit Bolt is all you get. Traits, ranks and its evolution still count.', mods: {}, rules: { skillSlots: 1 }, bossTime: 900, goal: { kind: 'survive', sec: 600 }, reward: 300, badge: chb('one', 300), brief: 'gold crest, a single bolt in an otherwise empty slot frame' },
  { id: 'twin', name: 'Twin Focus', desc: 'Only two skill slots, and elites start hunting you early.', mods: { elites: true }, rules: { skillSlots: 2 }, bossTime: 900, goal: { kind: 'win' }, reward: 350, badge: chb('two', 350), brief: 'prism crest, two slot frames linked by an energy arc' },
  { id: 'nightfall', name: 'Nightfall Horde', desc: 'Eternal Night, a Swarm and Early Elites, all at once.', mods: { night: true, swarm: true, elites: true }, bossTime: 900, goal: { kind: 'survive', sec: 720 }, reward: 300, badge: chb('moon', 300), brief: 'gold crest, a moon eclipsed by a cloud of glowing eyes' },
  { id: 'bare', name: 'Bare Resonance', desc: 'Power Ups off and no healing: just you and what you pick up.', mods: { noheal: true }, noPowerUps: true, bossTime: 900, goal: { kind: 'win' }, reward: 400, badge: chb('heart', 400), brief: 'prism crest, a bare resonance core with no armour around it' },
  { id: 'gauntlet', name: 'The Gauntlet', desc: 'All seven raise-the-stakes modifiers at once.', mods: Object.fromEntries(STAKE_MODS.map((id) => [id, true])) as RunMods, bossTime: 900, goal: { kind: 'survive', sec: 480 }, reward: 500, badge: chb('seven', 500), brief: 'prism crest, a gauntlet fist closed around seven lit pips' },
];
export const CHALLENGE_BY_ID: Record<string, ChallengeDef> = Object.fromEntries(CHALLENGES.map((c) => [c.id, c]));
export const goalText = (g: ChallengeGoal): string => (g.kind === 'win' ? 'Defeat the final boss' : `Survive ${Math.floor(g.sec / 60)}:${String(g.sec % 60).padStart(2, '0')}`);

// goals that depend on other tables
(ACH_BY_ID.chAll as { goal: number }).goal = CHALLENGES.length;
{
  let total = 0; for (const t of CODEX_TABS) total += counts(t, { enemies: {}, skills: {}, forms: {}, traits: {}, boons: {}, items: {}, fusions: {}, fresh: [] }).total;
  (ACH_BY_ID.codexHalf as { goal: number }).goal = Math.ceil(total / 2);
  (ACH_BY_ID.codexAll as { goal: number }).goal = total;
}

/** the Sim options a challenge run uses (merged over the normal run options by main.ts) */
export function challengeOptions(c: ChallengeDef): { mods: RunMods; rules: ChallengeRules; bossTime: number; endless: false; noPowerUps: boolean } {
  return { mods: { ...c.mods }, rules: { ...(c.rules ?? {}) }, bossTime: c.bossTime, endless: false, noPowerUps: !!c.noPowerUps };
}
/** does this Sim actually run under the challenge's rules? (only then can it be cleared) */
export function simMatches(sim: Sim, c: ChallengeDef): boolean {
  const on = modList(sim.mods).join(','), want = modList(c.mods).join(',');
  const r = sim.opts.rules ?? {}, w = c.rules ?? {};
  return on === want && (r.skillSlots ?? 0) === (w.skillSlots ?? 0) && (r.startSkills ?? 0) === (w.startSkills ?? 0)
    && sim.opts.bossTime === c.bossTime && (!c.noPowerUps || Object.keys(sim.opts.meta).length === 0);
}

// ---------------------------------------------------------------------------------------------------------------------
// Evaluation + rewards
// ---------------------------------------------------------------------------------------------------------------------
export interface Progress { cur: number; goal: number; done: boolean }
export function achProgress(a: AchDef, f: FeatsSave, codex: Codex): Progress {
  const done = f.unlocked[a.id] !== undefined, goal = Math.max(1, a.goal);
  return { cur: done ? goal : Math.min(goal, a.prog(f, codex)), goal, done };
}
export interface Payer { pay(amount: number, note: string): void }
/** Unlock every achievement whose goal is met (pays each reward once). Returns the newly unlocked ones. */
export function settle(f: FeatsSave, codex: Codex, payer: Payer, now: number, shardsHeld = 0): AchDef[] {
  if (shardsHeld >= HOARD_SHARDS) f.stats.hoard = 1;
  const out: AchDef[] = [];
  for (const a of ACHIEVEMENTS) {
    if (f.unlocked[a.id] !== undefined) continue;
    if (a.prog(f, codex) < Math.max(1, a.goal)) continue;
    f.unlocked[a.id] = now; f.fresh.push(a.id); out.push(a);
    if (a.reward > 0) { f.earned += a.reward; payer.pay(a.reward, `achievement ${a.id}`); }
  }
  return out;
}
/** Finish every quest whose goal is met: pay it and refill the slot. Returns the finished ones. */
export function settleQuests(f: FeatsSave, payer: Payer, day: string): QuestDef[] {
  const qs = f.quests, out: QuestDef[] = [];
  for (let guard = 0; guard < 12; guard++) {
    const i = qs.slots.findIndex((s) => { const d = QUEST_BY_ID[s.id]; return !!d && s.prog >= d.goal; });
    if (i < 0) break;
    const d = QUEST_BY_ID[qs.slots[i].id];
    out.push(d); qs.done++; f.earned += d.reward; payer.pay(d.reward, `quest ${d.id}`);
    if (d.kind === 'story' && STORY[qs.story]?.id === d.id) {
      qs.story++;
      const next = STORY[qs.story];
      if (next) { qs.slots[i] = { id: next.id, prog: 0 }; continue; }
    }
    qs.slots[i] = { id: dailyPick(qs.day || day, qs.n++, qs.slots.map((s) => s.id)).id, prog: 0 };
  }
  return out;
}

const addSet = (f: FeatsSave, k: SetKey, v: string): boolean => { const s = f.sets[k] ?? (f.sets[k] = []); if (s.includes(v)) return false; s.push(v); return true; };
/** add n to a lifetime sum (and to the active total-scope quests that count it) */
export function bump(f: FeatsSave, k: SumStat, n = 1): void {
  if (!(n > 0)) return;
  f.stats[k] = (f.stats[k] ?? 0) + n;
  for (const s of f.quests.slots) { const d = QUEST_BY_ID[s.id]; if (d && d.scope === 'total' && d.stat === k) s.prog += n; }
}
/** record a single-run value (keeps the best; run-scope quests keep their best since they were given) */
export function runBest(f: FeatsSave, k: RunStat, v: number): void {
  if (v > (f.stats[k] ?? 0)) f.stats[k] = v;
  for (const s of f.quests.slots) { const d = QUEST_BY_ID[s.id]; if (d && d.scope === 'run' && d.stat === k && v > s.prog) s.prog = v; }
}
const flag = (f: FeatsSave, k: FlagStat) => { f.stats[k] = 1; };

// ---------------------------------------------------------------------------------------------------------------------
// Run tracker: call step(sim) after every sim.step(), afterChoice(sim) after sim.choose()/chooseBoon(), endRun() once.
// ---------------------------------------------------------------------------------------------------------------------
/** TRAINING (SimOptions.training: a TrainingOptions object, src/core/training.ts; sim.training = the live sandbox): such runs
 *  never progress achievements, quests or challenges */
export const isTrainingRun = (sim: Sim): boolean => !!sim.practice || !!(sim.opts as { training?: unknown }).training; // (practice = training or the Basics lesson)

export interface FeatHost extends Payer {
  feats(): FeatsSave;
  codex(): Codex;
  /** wallet right now (Shard Hoard) */
  shards(): number;
  /** wall clock (ms since epoch) — injected for tests */
  now(): number;
  onUnlock?(a: AchDef): void;
  onQuest?(d: QuestDef): void;
  onChallenge?(c: ChallengeDef, first: boolean): void;
}
export interface RunContext { training?: boolean; challenge?: string | null; powerUps?: boolean }
export interface QuestLine { def: QuestDef; prog: number; goal: number; done: boolean; gained: number }
export interface FeatSummary {
  unlocked: AchDef[]; quests: QuestLine[];
  challenge: { def: ChallengeDef; done: boolean; first: boolean } | null;
  /** shards paid by achievements, quests and challenges during this run */
  shards: number;
}
/** check achievements / quests every this many steps (0.5 s) */
const CHECK_EVERY = 30;

export class FeatTracker {
  /** false: not tracking (no run yet, a training run, or the run has been ended) */
  active = false;
  /** something changed that should be saved */
  dirty = false;
  private sim: Sim | null = null;
  private ch: ChallengeDef | null = null;
  private chDone = false;
  private powerUps = true;
  private evDone = 0; private n = 0;
  private kills = 0; private caches = 0; private surges = 0; private evo = new Set<string>();
  private eliteT: number[] = [];
  private dmg = 0; private sec = 0;
  private won = false; private hurt = false;
  private stillT = 0; private lx = 0; private lz = 0; private lowAt = -1;
  private unlocked: AchDef[] = []; private done: QuestDef[] = [];
  private qBefore = new Map<string, number>();
  private earned0 = 0;

  constructor(private host: FeatHost) {}
  private get f(): FeatsSave { return this.host.feats(); }

  beginRun(sim: Sim, ctx: RunContext = {}): void {
    this.sim = sim; this.evDone = 0; this.n = 0;
    this.kills = 0; this.caches = 0; this.surges = 0; this.evo = new Set(sim.evolved); this.eliteT = []; this.dmg = 0; this.sec = 0;
    this.won = false; this.hurt = false; this.stillT = 0; this.lx = sim.player.x; this.lz = sim.player.z; this.lowAt = -1;
    this.unlocked = []; this.done = [];
    // TRAINING: no progress of any kind (also when the Sim itself says so)
    this.active = !(ctx.training || isTrainingRun(sim));
    this.ch = ctx.challenge ? CHALLENGE_BY_ID[ctx.challenge] ?? null : null; this.chDone = false;
    this.powerUps = ctx.powerUps ?? Object.keys(sim.opts.meta).length > 0;
    if (!this.active) { this.ch = null; return; }
    const f = this.f;
    rollQuests(f.quests, dayKey(new Date(this.host.now())));
    this.qBefore = new Map(f.quests.slots.map((s) => [s.id, s.prog]));
    this.earned0 = f.earned;
    if (this.ch) { const r = f.challenges[this.ch.id] ?? (f.challenges[this.ch.id] = { tries: 0, done: 0, first: 0, best: 0 }); r.tries++; }
    this.dirty = true;
  }

  step(sim: Sim): void {
    if (!this.active || sim !== this.sim) return;
    this.evDone = 0;
    this.events(sim);
    this.scan(sim);
    if (++this.n % CHECK_EVERY === 0) this.check();
  }
  afterChoice(sim: Sim): void {
    if (!this.active || sim !== this.sim) return;
    if (this.evDone > sim.events.length) this.evDone = 0;
    this.events(sim); this.scan(sim); this.check();
  }

  private events(sim: Sim): void {
    const f = this.f, evs = sim.events;
    for (let i = this.evDone; i < evs.length; i++) {
      const ev = evs[i];
      switch (ev.t) {
        case 'kill':
          this.kills++; bump(f, 'kills'); runBest(f, 'runKills', this.kills);
          if (ev.boss) bump(f, 'bosses');
          else if (ev.elite) {
            bump(f, 'elites');
            this.eliteT.push(sim.time); while (this.eliteT.length && sim.time - this.eliteT[0] > ELITE_WINDOW) this.eliteT.shift();
            runBest(f, 'elites10', this.eliteT.length);
          }
          break;
        case 'cacheOpen': this.caches++; bump(f, 'caches'); runBest(f, 'runCaches', this.caches); break;
        case 'chestOpen': bump(f, 'chests'); break;
        case 'fieldPick': bump(f, 'fields'); break;
        case 'transform': this.surges++; bump(f, 'surges'); runBest(f, 'runForm', ev.tier); if (ev.tier >= 3) flag(f, 'surge3'); if (ev.tier >= 4) flag(f, 'surge4'); if (ev.tier >= 6) flag(f, 'surge6'); if (ev.tier >= 8) flag(f, 'surge8'); break; // SURGE FORMS
        case 'fusion': bump(f, 'fusions'); addSet(f, 'fused', ev.f); break;
        case 'hurt': case 'hazardHurt': this.hurt = true; break;
        case 'endlessTier': runBest(f, 'endlessTier', ev.tier); break;
        case 'victory': this.win(sim); break;
      }
    }
    this.evDone = evs.length;
    this.dirty = true;
  }

  private scan(sim: Sim): void {
    const f = this.f, p = sim.player;
    // evolutions (a fused partner leaves sim.evolved; the set remembers it for this run)
    for (const id of sim.evolved) if (!this.evo.has(id)) { this.evo.add(id); bump(f, 'evolves'); addSet(f, 'evolved', id); runBest(f, 'runEvolves', this.evo.size); }
    runBest(f, 'runLevel', p.level);
    runBest(f, 'runTime', Math.floor(sim.time));
    const s = Math.floor(sim.time); if (s > this.sec) { bump(f, 'seconds', s - this.sec); this.sec = s; }
    if (sim.endless && sim.victoryAt > 0) { runBest(f, 'endless', Math.floor(sim.endlessTime)); runBest(f, 'endlessTier', sim.endlessTier); }
    // build
    const slots = sim.techSlotsUsed;
    if (slots >= MAX_WEAPONS) {
      flag(f, 'slots6');
      let maxed = true;
      for (const [id, lv] of sim.weapons) if (!INNATE.includes(id) && lv < MAX_WEAPON_LEVEL && !sim.evolved.has(id)) maxed = false;
      if (maxed) flag(f, 'maxed6');
    }
    // damage (every check: dmgBySrc is ~30 entries)
    if (this.n % CHECK_EVERY === 0) {
      let d = 0; for (const k in sim.dmgBySrc) d += sim.dmgBySrc[k] ?? 0;
      if (d > this.dmg) { bump(f, 'damage', d - this.dmg); this.dmg = d; runBest(f, 'runDamage', Math.floor(d)); }
    }
    // secrets
    if (sim.state !== 'dead') {
      const moved = Math.abs(p.x - this.lx) + Math.abs(p.z - this.lz) > 1e-6;
      this.lx = p.x; this.lz = p.z;
      if (sim.state === 'playing') this.stillT = moved ? 0 : this.stillT + DT;
      if (this.stillT >= STILL_SEC) flag(f, 'still');
      if (p.hp > 0 && p.hp <= p.maxHp * THREAD_HP && this.lowAt < 0) this.lowAt = sim.time;
      if (this.lowAt >= 0 && p.hp > 0 && sim.time - this.lowAt >= THREAD_SEC) flag(f, 'thread');
      if (!this.hurt && sim.time >= UNTOUCHED_SEC) flag(f, 'untouched');
    }
    // challenge (survive goal)
    if (this.ch && !this.chDone && this.ch.goal.kind === 'survive') {
      const r = f.challenges[this.ch.id];
      if (r) r.best = Math.max(r.best, Math.min(1, sim.time / this.ch.goal.sec));
      if (sim.time >= this.ch.goal.sec && sim.state !== 'dead') this.clearChallenge(sim);
    }
  }

  /** the final boss fell (once per run) */
  private win(sim: Sim): void {
    if (this.won) return; this.won = true;
    const f = this.f;
    if (sim.opts.bossTime >= MIN_WIN_LEN) {
      bump(f, 'wins');
      addSet(f, 'mapsWon', sim.map.id);
      if (sim.tod === 'night') flag(f, 'winNight');
      // modifier + difficulty achievements: never with Relaxed on (winDifficulty → null)
      const diff = winDifficulty(sim.mods);
      if (diff) {
        const mods = modList(sim.mods);
        for (const id of mods) addSet(f, 'modsWon', id);
        if (mods.length >= 3) flag(f, 'mods3');
        if (STAKE_MODS.every((id) => mods.includes(id))) flag(f, 'modsAll');
        if (diff !== 'custom') addSet(f, 'diffWon', diff);
        if (diff === 'brutal') { addSet(f, 'diffWon', 'hard'); addSet(f, 'brutalMaps', sim.map.id); } // Brutal contains Hard
      }
      if (this.evo.size >= 3 || sim.evolved.size >= 3) flag(f, 'winEvo3');
      if (sim.fusion.owned.size > 0) flag(f, 'winFusion');
      if (this.surges === 0) flag(f, 'winNoSurge');
      if (!this.powerUps) flag(f, 'winNoPower');
      if (sim.opts.bossTime >= 1800) flag(f, 'win30');
    }
    if (this.ch && !this.chDone && this.ch.goal.kind === 'win') this.clearChallenge(sim);
    this.check();
  }

  private clearChallenge(sim: Sim): void {
    const c = this.ch!; if (!simMatches(sim, c)) return;
    this.chDone = true;
    const f = this.f, r = f.challenges[c.id] ?? (f.challenges[c.id] = { tries: 1, done: 0, first: 0, best: 0 });
    const first = r.done === 0;
    r.done++; r.best = 1;
    if (first) { r.first = this.host.now(); addSet(f, 'challenges', c.id); f.earned += c.reward; this.host.pay(c.reward, `challenge ${c.id}`); }
    bump(f, 'challenges');
    this.host.onChallenge?.(c, first);
    this.check();
  }

  /** settle achievements and quests now */
  check(): void {
    if (!this.active) return;
    const f = this.f, now = this.host.now();
    for (const a of settle(f, this.host.codex(), this.host, now, this.host.shards())) { this.unlocked.push(a); this.host.onUnlock?.(a); }
    for (const d of settleQuests(f, this.host, dayKey(new Date(now)))) { this.done.push(d); this.host.onQuest?.(d); }
  }

  /** the run is over (death, victory or quit): final tallies + the results-screen summary. Training: null. */
  endRun(sim: Sim, victory: boolean): FeatSummary | null {
    if (!this.active || sim !== this.sim) return null;
    this.scan(sim); // (this step's events were already read by step())
    if (victory) this.win(sim);
    const f = this.f;
    bump(f, 'runs'); addSet(f, 'mapsPlayed', sim.map.id);
    if (!sim.mods.relaxed && modList(sim.mods).length) bump(f, 'modRuns'); // Relaxed never raises the stakes
    const h = new Date(this.host.now()).getHours(); if (h >= 0 && h < 4) flag(f, 'late');
    this.check();
    this.active = false; this.dirty = true;
    const quests: QuestLine[] = this.done.map((d) => ({ def: d, prog: d.goal, goal: d.goal, done: true, gained: 0 }));
    for (const s of f.quests.slots) {
      const d = QUEST_BY_ID[s.id]; if (!d) continue;
      const before = this.qBefore.get(s.id);
      quests.push({ def: d, prog: Math.min(d.goal, s.prog), goal: d.goal, done: false, gained: before === undefined ? s.prog : Math.max(0, s.prog - before) });
    }
    const c = this.ch;
    return { unlocked: [...this.unlocked], quests, challenge: c ? { def: c, done: this.chDone, first: this.chDone && f.challenges[c.id]?.done === 1 } : null, shards: f.earned - this.earned0 };
  }
}

/** number formatting for progress (1.2k / 3.4M) */
export function short(n: number): string {
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`;
  if (n >= 1e4) return `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1).replace(/\.0$/, '')}k`;
  return Math.floor(n).toLocaleString('en-US');
}
/** progress text: time goals as m:ss */
export function progText(cur: number, goal: number, timeLike: boolean): string {
  const t = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  return timeLike ? `${t(cur)} / ${t(goal)}` : `${short(cur)} / ${short(goal)}`;
}
export const TIME_ACH: ReadonlySet<string> = new Set(['surv5', 'surv10', 'surv20', 'surv30', 'endless10']);
export const TIME_QUEST = (d: QuestDef): boolean => d.stat === 'runTime' || d.stat === 'seconds';
